import React, { useContext, useMemo, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import localeDatas from "antd/es/date-picker/locale/pt_BR";
import { Consult } from "../../components/Consult";
import dayjs from "dayjs";
import { Button, DatePicker, Modal, Space, Tag, Typography } from "antd";
import { request } from "../../functions/request";
import { Content } from "antd/es/layout/layout";
import { AiFillEdit, AiFillEye } from "react-icons/ai";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCalendarCheck, FaArrowAltCircleUp, FaCopy, FaClock, FaTrash } from "react-icons/fa";

const aprovada = (n) => n?.not_aprovado === true;

// Até quantos minutos para trás um horário ainda conta como "agora".
const TOLERANCIA_AGORA_MIN = 10;
/** Horário que já chegou (ou chega em menos de 1 minuto): publica na hora. */
const ehAgora = (d) => Boolean(d) && !d.isAfter(dayjs().add(1, "minute"));

const { Text } = Typography;

/**
 * Copia texto para a área de transferência.
 *
 * O navigator.clipboard só existe em página segura (https/localhost); no
 * acesso pela rede interna em http ele é undefined. Aí cai no jeito antigo,
 * com um textarea temporário.
 */
async function copiarTexto(texto) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(texto);
    return;
  }
  const area = document.createElement("textarea");
  area.value = texto;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(area);
  if (!ok) throw new Error("cópia recusada pelo navegador");
}

/** Horário sugerido para agendar: a próxima meia hora cheia. */
const proximaMeiaHora = () => {
  const agora = dayjs();
  const base = agora.minute() < 30 ? agora.minute(30) : agora.add(1, "hour").minute(0);
  return base.second(0);
};

export default function ConsultNoticiasInativas() {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState([]);
  const [modalContent, setModalContent] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agendando, setAgendando] = useState(null); // notícia aberta no modal de agendamento
  const [quando, setQuando] = useState(null);
  const [salvandoAgenda, setSalvandoAgenda] = useState(false);
  const columns = useMemo(
    () => [
      {
        accessorKey: "not_numero",
        header: "ID",
        size: 40,
      },
      {
        accessorKey: "not_titulo",
        header: "Título",
        size: 60,
        // A tag deixa a aprovação visível sem depender só da cor da linha.
        Cell: ({ row, renderedCellValue }) => (
          <>
            {aprovada(row.original) && (
              <Tag color="green" style={{ marginRight: 6 }}>
                Aprovada
              </Tag>
            )}
            {renderedCellValue}
          </>
        ),
      },
      {
        accessorKey: "not_lead",
        header: "Lead",
        size: 100,
      },
      {
        accessorKey: "not_data",
        header: "Data",
        size: 60,
        // A coluna mostra "DD/MM/YYYY HH:mm"; ordenar esse texto põe
        // 31/08 depois de 01/10. A ordem vem da data no formato ISO.
        sortingFn: (a, b) =>
          String(a.original.not_data_iso).localeCompare(String(b.original.not_data_iso)),
      },
      {
        accessorKey: "not_secao",
        header: "Seção",
        size: 60,
        filterVariant: "multi-select",
      },
    ],
    []
  );

  const transformSectionComent = (section) => {
    switch (section) {
      case 1:
        return "Boi";
      case 2:
        return "Frango";
      case 3:
        return "Grãos";
      case 4:
        return "Leite";
      case 5:
        return "Suínos";
      case 8:
        return "Agroações";
      case 9:
        return "Bolsas e Opções";
      default:
        return "Outros";
    }
  };
  // Antes as duas ações avisavam "sucesso" num finally, mesmo quando a
  // requisição falhava.

  /** Põe no ar agora. Só existe para matéria aprovada. */
  const publicar = (noticia) => {
    Modal.confirm({
      title: "Publicar agora no site?",
      content: noticia.not_titulo,
      okText: "Publicar",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          // A mesma rota de "Publicar agora" dos Agendados: além de ativar,
          // carimba a hora da publicação, para a matéria entrar no topo do
          // site em vez de sair com a hora em que foi salva como rascunho.
          await request(`/noticia/publicarAgoraNoticia/${noticia.not_numero}`, { method: "PUT" });
          toast.success("Notícia publicada.");
          fetchData();
        } catch (erro) {
          console.log(erro);
          toast.error("Não foi possível publicar a notícia.");
        }
      },
    });
  };

  /** Só marca como aprovada para publicação. Não põe no ar. */
  const aprovar = async (not_numero) => {
    try {
      await request(`/noticia/updateNoticiasAproval/${not_numero}`, { method: "PUT" });
      toast.success("Aprovada. Ainda não está no ar: use Publicar ou Agendar.");
      fetchData();
    } catch (erro) {
      console.log(erro);
      toast.error("Não foi possível aprovar a notícia.");
    }
  };

  const abrirAgendamento = (noticia) => {
    setAgendando(noticia);
    setQuando(proximaMeiaHora());
  };

  const aprovarEAgendar = async () => {
    if (!quando) return;
    // "Agora" no calendário (ou um horário de minutos atrás, o tempo de
    // clicar) é publicar agora — antes era recusado como "passado" e a
    // notícia ficava parada na aprovação. Horário bem no passado continua
    // recusado: publicar sem querer é pior do que pedir outra data.
    if (quando.isBefore(dayjs().subtract(TOLERANCIA_AGORA_MIN, "minute"))) {
      toast.error("Esse horário já passou. Escolha uma data futura, ou \"Agora\" para publicar já.");
      return;
    }
    setSalvandoAgenda(true);
    if (ehAgora(quando)) {
      try {
        if (!aprovada(agendando)) {
          await request(`/noticia/updateNoticiasAproval/${agendando.not_numero}`, { method: "PUT" });
        }
        // Mesma rota do "Publicar": põe no ar com a hora do servidor.
        await request(`/noticia/publicarAgoraNoticia/${agendando.not_numero}`, { method: "PUT" });
        toast.success("Aprovada e publicada agora.");
        setAgendando(null);
        fetchData();
      } catch (erro) {
        toast.error("Não foi possível publicar a notícia.");
        console.log(erro);
      } finally {
        setSalvandoAgenda(false);
      }
      return;
    }
    try {
      // Texto, e não Date: o servidor converte no SQL, sem mexer no fuso.
      await request(`/noticia/aprovarAgendarNoticia/${agendando.not_numero}`, {
        method: "PUT",
        body: JSON.stringify({ not_data: quando.format("YYYY-MM-DD HH:mm") }),
      });
      toast.success(`Aprovada e agendada para ${quando.format("DD/MM/YYYY [às] HH:mm")}. Está em Agendados.`);
      setAgendando(null);
      fetchData();
    } catch (erro) {
      toast.error("Não foi possível agendar. Confira a data e tente de novo.");
      console.log(erro);
    } finally {
      setSalvandoAgenda(false);
    }
  };

  // Excluir só aparece para quem tem a permissão "Excluir" no cadastro de
  // usuários. A API confere de novo (a tela esconder o botão não basta).
  const { accessLevel } = useContext(AuthContext);
  const podeExcluir = Boolean(accessLevel?.usr_excluir);

  /** Exclui uma ou várias notícias, depois de confirmar. */
  const excluir = (noticias, aoConcluir) => {
    const n = noticias.length;
    Modal.confirm({
      title: n === 1 ? "Excluir esta notícia?" : `Excluir ${n} notícias?`,
      content: (
        <div>
          <ul style={{ paddingLeft: 18, margin: "8px 0", maxHeight: 200, overflowY: "auto" }}>
            {noticias.map((x) => (
              <li key={x.not_numero}>{x.not_titulo}</li>
            ))}
          </ul>
          <Text type="danger">Não dá para desfazer.</Text>
        </div>
      ),
      okText: "Excluir",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          const r = await request("/noticia/excluirNoticias", {
            method: "POST",
            body: JSON.stringify({ ids: noticias.map((x) => x.not_numero) }),
          });
          const feitas = r?.excluidas?.length ?? 0;
          if (r?.ignoradas?.length) {
            toast.warning(`${feitas} excluída(s). ${r.ignoradas.length} já estava(m) no ar e não foi(ram) excluída(s).`);
          } else {
            toast.success(feitas === 1 ? "Notícia excluída." : `${feitas} notícias excluídas.`);
          }
          if (aoConcluir) aoConcluir();
          fetchData();
        } catch (erro) {
          console.log(erro);
          toast.error(
            /Forbidden/i.test(String(erro))
              ? "Você não tem permissão para excluir notícias."
              : "Não foi possível excluir."
          );
        }
      },
    });
  };

  const aprovarECopiarTitulos = async (linhas, table) => {
    const texto = linhas
      // Título + lead: títulos como "Bolsa B3" se repetem e, sozinhos, não dizem qual é a matéria.
      .map(({ original: n }) => `• ${n.not_titulo}${n.not_lead ? ` — ${n.not_lead}` : ""}`)
      .join("\n");

    // Copia ANTES de aprovar: o navegador só libera a área de transferência
    // logo depois do clique, e as aprovações podem levar alguns segundos.
    let copiou = true;
    try {
      await copiarTexto(texto);
    } catch (erro) {
      copiou = false;
    }

    const pendentes = linhas.map((l) => l.original).filter((n) => !aprovada(n));
    let falhas = 0;
    for (const n of pendentes) {
      try {
        await request(`/noticia/updateNoticiasAproval/${n.not_numero}`, { method: "PUT" });
      } catch (erro) {
        console.log(erro);
        falhas++;
      }
    }
    const aprovadas = pendentes.length - falhas;

    const partes = [
      copiou ? `${linhas.length} título(s) copiado(s)` : "O navegador não deixou copiar",
      `${aprovadas} aprovada(s)`,
    ];
    if (falhas) partes.push(`${falhas} não aprovada(s) por erro`);
    (copiou && !falhas ? toast.success : toast.error)(`${partes.join(", ")}.`);

    table.resetRowSelection();
    if (pendentes.length) fetchData();
  };

  const fetchData = async () => {
    try {
      setHasError(false);
      setIsLoading(true);
      await request(`/noticia/getnoticiasapproval`, {
        method: "GET",
      })
        .then((response) => {
          const arr = Array.isArray(response) ? response : response?.[0];
          if (!Array.isArray(arr)) {
            setData([]);
            setHasError(true);
            return;
          }
          const formattedData = arr
            .map((res) => {
              res.not_data_iso = String(res?.not_data || "");
              res.not_data = dayjs(res?.not_data).format("DD/MM/YYYY HH:mm");
              res.not_secao = transformSectionComent(res?.not_secao);
              return res;
            })
            // A API já manda nessa ordem; garantido aqui também, para não
            // depender de quem mexer na consulta.
            .sort((a, b) => b.not_data_iso.localeCompare(a.not_data_iso) || b.not_numero - a.not_numero);
          if (formattedData.length <= 0) {
            setData([]);
            setHasError(true);
          } else {
            setData(formattedData);
          }
        })
        .finally(() => {
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setData([]);
          setIsLoading(false);
          setHasError(true);
        });
    } catch (error) {
      console.error("Erro na requisição:", error);
      setData([]);
      setIsLoading(false);
      setHasError(true);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <Content>
      <Consult.Table
        data={data}
        columns={columns}
        isLoading={isLoading}
        hasError={hasError}
        style={{ width: "100%", minWidth: "100%" }}
        enableRowSelection
        getRowId={(row) => String(row.not_numero)}
        // Aprovadas em verde. Selecionada mantém a cor de seleção da tabela.
        muiTableBodyRowProps={({ row }) =>
          aprovada(row.original)
            ? {
                "data-aprovada": "true",
                sx: {
                  boxShadow: "inset 4px 0 0 #52c41a",
                  ...(row.getIsSelected() ? {} : { "& td": { backgroundColor: "#f0faf0" } }),
                },
              }
            : {}
        }
        initialState={{
          showColumnFilters: true,
          pagination: { pageIndex: 0, pageSize: 100 },
          sorting: [{ id: "not_data", desc: true }],
        }}
        renderTopToolbarCustomActions={({ table }) => {
          const selecionadas = table.getSelectedRowModel().rows;
          return (
            <Space wrap>
              <Button
                icon={<FaCopy />}
                disabled={!selecionadas.length}
                onClick={() => aprovarECopiarTitulos(selecionadas, table)}
              >
                Aprovar e copiar títulos{selecionadas.length ? ` (${selecionadas.length})` : ""}
              </Button>
              {podeExcluir && (
                <Button
                  danger
                  icon={<FaTrash />}
                  disabled={!selecionadas.length}
                  onClick={() => excluir(selecionadas.map((l) => l.original), () => table.resetRowSelection())}
                >
                  Excluir selecionadas{selecionadas.length ? ` (${selecionadas.length})` : ""}
                </Button>
              )}
              {selecionadas.length > 0 && (
                <Button type="link" onClick={() => table.resetRowSelection()}>
                  Limpar seleção
                </Button>
              )}
            </Space>
          );
        }}
        renderRowActions={({ row }) => (
          <Content
            style={{
              display: "flex",
              gap: "5px",
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <Button
              onClick={() => {
                setModalContent(row.original);
                setIsModalOpen(true);
              }}
              icon={<AiFillEye />}
            >
              Visualizar
            </Button>
            <Link to={`/editor-noticia/${row.original.not_numero}`}>
              <Button icon={<AiFillEdit />}>Editar</Button>
            </Link>
            {/* Aprovar só marca; Publicar põe no ar e só aparece depois de aprovada. */}
            {aprovada(row.original) ? (
              <Button
                color="default"
                variant="solid"
                icon={<FaArrowAltCircleUp />}
                onClick={() => publicar(row.original)}
              >
                Publicar
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<FaCalendarCheck />}
                onClick={() => aprovar(row.original.not_numero)}
              >
                Aprovar
              </Button>
            )}
            <Button
              color="gold"
              variant="solid"
              icon={<FaClock />}
              onClick={() => abrirAgendamento(row.original)}
            >
              {aprovada(row.original) ? "Agendar" : "Aprovar e agendar"}
            </Button>
            {podeExcluir && (
              <Button danger icon={<FaTrash />} onClick={() => excluir([row.original])}>
                Excluir
              </Button>
            )}
          </Content>
        )}
      />
      <Consult.Modal
        title={modalContent?.not_titulo}
        date={modalContent?.not_data}
        text={modalContent?.not_texto}
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
      />
      <Modal
        title={aprovada(agendando) ? "Agendar publicação" : "Aprovar e agendar"}
        open={Boolean(agendando)}
        onCancel={() => setAgendando(null)}
        onOk={aprovarEAgendar}
        okText={
          ehAgora(quando)
            ? aprovada(agendando) ? "Publicar agora" : "Aprovar e publicar agora"
            : aprovada(agendando) ? "Agendar" : "Aprovar e agendar"
        }
        cancelText="Cancelar"
        confirmLoading={salvandoAgenda}
        okButtonProps={{ disabled: !quando }}
        destroyOnHidden
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text strong>{agendando?.not_titulo}</Text>
          {agendando?.not_lead && <Text type="secondary">{agendando.not_lead}</Text>}
          <DatePicker
            showTime={{ format: "HH:mm", minuteStep: 5 }}
            locale={localeDatas}
            format="DD/MM/YYYY HH:mm"
            placeholder="Data e hora da publicação"
            value={quando}
            onChange={setQuando}
            disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
            style={{ width: "100%" }}
            aria-label="Data e hora da publicação"
          />
          <Text type="secondary">
            {ehAgora(quando)
              ? "Horário de agora: a notícia é aprovada e vai para o site imediatamente."
              : "A notícia é aprovada agora, sai desta lista e vai para Agendados. Ela entra no ar sozinha no horário marcado."}
          </Text>
        </Space>
      </Modal>
    </Content>
  );
}
